import React, { useState, useEffect, useCallback, useRef, memo } from "react";
import { Grid as Grid2, Paper, Box, Skeleton } from "@mui/material";
import { styled } from "@mui/material/styles";
import LikeButton from "../likebutton.js";
import { useAuth } from "../../contexts/AuthContext";
import ImageGalleryModal from "./ImageGalleryModal"; // Import the new modal component
import axios from "axios";

// Styled components (unchanged)
const StyledPaper = styled(Paper)(({ theme }) => ({
  /* ... */
}));
const ImageContainer = styled(Box)(({ theme }) => ({
  /* ... */
}));
const LikeButtonContainer = styled(Box)(({ theme }) => ({
  /* ... */
}));

// Cache for API responses
const imageCache = new Map();

// Skeleton placeholders
const SKELETON_COUNT = 6;

const CardMediaImages = ({ divesiteId }) => {
  const [mediaImagesData, setMediaImagesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const abortControllerRef = useRef(null);
  const { currentUser, userObjectId, userClaims } = useAuth();

  // Track like state changes
  const likeStateRef = useRef(new Map());

  // Determine image size based on claims
  const getImageSizeFromClaims = () => {
    if (!userClaims) {
      console.log(
        "CardMediaImages - User claims not available, defaulting to small image size."
      );
      return "small"; // Default to small (assuming tier 1/2 default)
    }
    if (userClaims.isTier5User || userClaims.isTier6User) {
      console.log(
        "CardMediaImages - User is Tier 5 or 6, using large image size."
      );
      return "large";
    }
    if (userClaims.isTier3User || userClaims.isTier4User) {
      console.log(
        "CardMediaImages - User is Tier 3 or 4, using medium image size."
      );
      return "medium";
    }
    console.log(
      "CardMediaImages - User tier is 1 or 2 (or not matched), using small image size."
    );
    return "small";
  };

  const imageSize = getImageSizeFromClaims();
  console.log("CardMediaImages - Determined image size:", imageSize);

  useEffect(() => {
    if (!divesiteId) {
      console.log("CardMediaImages - No divesiteId provided, skipping fetch.");
      return;
    }
    if (abortControllerRef.current) {
      console.log("CardMediaImages - Aborting previous fetch.");
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    const fetchMediaImages = async () => {
      const cacheKey = `media-images-${divesiteId}`;
      console.log(
        `CardMediaImages - Attempting to fetch images for divesite: ${divesiteId}`
      );
      if (imageCache.has(cacheKey)) {
        const cachedData = imageCache.get(cacheKey);
        console.log(
          `CardMediaImages - Found data in cache for ${cacheKey}:`,
          cachedData
        );
        setMediaImagesData(cachedData);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      console.log(
        `CardMediaImages - Fetching media images from API for ${divesiteId}...`
      );

      try {
        const token = await currentUser.getIdToken();
        console.log("CardMediaImages - Firebase ID token:", token);

        // Axios request
        const response = await axios.get(
          `/api/media/divesite/${divesiteId}`,
          {
            signal: abortControllerRef.current.signal,
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        console.log("CardMediaImages - Fetch response:", response);

        // With Axios, you access the data directly from response.data
        // No need to check response.ok or call response.json()
        const data = response.data;
        console.log("CardMediaImages - Fetched media data:", data);

        const filteredMedia = data.filter(
          (item) =>
            item.mediaType === "image" &&
            !(item.tags && item.tags.includes("hero")) &&
            !(item.tags && item.tags.includes("diagram"))
        );
        console.log(
          "CardMediaImages - Filtered media (excluding hero and diagram):",
          filteredMedia
        );

        // Add liked property to each media item
        const enhancedMedia = filteredMedia.map((item) => ({
          ...item,
          liked: false, // Default state
        }));

        imageCache.set(cacheKey, enhancedMedia);
        setMediaImagesData(enhancedMedia);
        console.log("CardMediaImages - mediaImagesData:", enhancedMedia);
      } catch (error) {
        if (error.name !== "AbortError") {
          setError(error);
          console.error("CardMediaImages - Fetch error:", error);
          // Provide more details about Axios errors
          if (error.response) {
            // The request was made and the server responded with a status code
            // that falls out of the range of 2xx
            console.error(
              "CardMediaImages - Error response data:",
              error.response.data
            );
            console.error(
              "CardMediaImages - Error response status:",
              error.response.status
            );
            console.error(
              "CardMediaImages - Error response headers:",
              error.response.headers
            );
          } else if (error.request) {
            // The request was made but no response was received
            console.error(
              "CardMediaImages - No response received:",
              error.request
            );
          } else {
            // Something happened in setting up the request that triggered an Error
            console.error(
              "CardMediaImages - Request setup error:",
              error.message
            );
          }
        } else {
          console.log("CardMediaImages - Fetch aborted.");
        }
      } finally {
        setLoading(false);
        console.log("CardMediaImages - Fetch media images process completed.");
      }
    };

    fetchMediaImages();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [divesiteId, currentUser]);

  // Preload images
  useEffect(() => {
    if (mediaImagesData.length > 0) {
      mediaImagesData.forEach((item) => {
        const imageUrl = item.mediaUrl?.[imageSize]; // Dynamically access the URL based on imageSize

        if (imageUrl) {
          console.log(
            `CardMediaImages - Preloading image (${imageSize}): ${imageUrl}`
          );
          const img = new Image();
          img.src = imageUrl;
        }
      });
    }
  }, [mediaImagesData, imageSize]);

  const handleImageClick = useCallback((index, event) => {
    if (
      event.target.closest(".like-button-container") ||
      event.target.closest("button[aria-label='Like']") ||
      event.target.closest("button[aria-label='Unlike']")
    ) {
      console.log(
        `CardMediaImages - Like button clicked on image index: ${index}, not opening modal.`
      );
      return;
    }
    console.log(
      `CardMediaImages - Image clicked, opening modal for index: ${index}`
    );
    setSelectedImageIndex(index);
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    console.log("CardMediaImages - Closing modal.");
    setOpen(false);
    setSelectedImageIndex(null);
  }, []);

  const handlePrev = useCallback(() => {
    setSelectedImageIndex((prevIndex) => {
      const newIndex =
        prevIndex > 0 ? prevIndex - 1 : mediaImagesData.length - 1;
      console.log(`CardMediaImages - Previous image, new index: ${newIndex}`);
      return newIndex;
    });
  }, [mediaImagesData]);

  const handleNext = useCallback(() => {
    setSelectedImageIndex((prevIndex) => {
      const newIndex =
        prevIndex < mediaImagesData.length - 1 ? prevIndex + 1 : 0;
      console.log(`CardMediaImages - Next image, new index: ${newIndex}`);
      return newIndex;
    });
  }, [mediaImagesData]);

  // Update like status in the mediaImagesData directly
  const handleLikeChange = useCallback((itemId, isLiked) => {
    console.log(
      `CardMediaImages - Image ${itemId} like status changed to: ${isLiked}`
    );
    likeStateRef.current.set(itemId, isLiked);
    setMediaImagesData((prevData) =>
      prevData.map((item) =>
        item._id === itemId ? { ...item, liked: isLiked } : item
      )
    );
  }, []);

  if (loading) {
    return (
      <StyledPaper>
        <Grid2 container spacing={2}>
          {Array.from({ length: SKELETON_COUNT }).map((_, index) => (
            <Grid2 item xs={12} sm={6} md={4} key={`skeleton-${index}`}>
              <ImageContainer>
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height="100%"
                  animation="wave"
                  sx={{ bgcolor: "grey.200" }}
                />
              </ImageContainer>
            </Grid2>
          ))}
        </Grid2>
      </StyledPaper>
    );
  }

  if (error) {
    return <p>Error loading media images: {error.message}</p>;
  }

  if (mediaImagesData.length === 0) {
    return null;
  }

  return (
    <StyledPaper>
      <Grid2 container spacing={2}>
        {mediaImagesData.map((item, index) => {
          const imageUrl = item.mediaUrl?.[imageSize]; // Dynamically access the URL

          const itemId = item._id;
          const isLiked =
            item.liked || likeStateRef.current.get(itemId) || false;

          return (
            imageUrl && ( // Check if imageUrl is valid
              <Grid2 item xs={12} sm={6} md={4} key={`grid-${itemId || index}`}>
                <ImageContainer onClick={(e) => handleImageClick(index, e)}>
                  <img
                    src={imageUrl}
                    alt="scuba diving thematic imagery"
                    loading={index < 6 ? "eager" : "lazy"}
                    decoding="async"
                    width="100%"
                    height="100%"
                  />
                  {currentUser && userObjectId && (
                    <LikeButtonContainer className="like-button-container">
                      <LikeButton
                        userId={userObjectId}
                        itemId={itemId}
                        onLikeChange={handleLikeChange}
                        initialLiked={isLiked}
                        key={`grid-like-${itemId}-${isLiked}`}
                      />
                    </LikeButtonContainer>
                  )}
                </ImageContainer>
              </Grid2>
            )
          );
        })}
      </Grid2>

      <ImageGalleryModal
        open={open}
        handleClose={handleClose}
        mediaImages={mediaImagesData}
        startIndex={selectedImageIndex}
        imageSize={imageSize}
        handlePrev={handlePrev}
        handleNext={handleNext}
        currentUser={currentUser}
        userObjectId={userObjectId}
        handleLikeChange={handleLikeChange}
        likeStateRef={likeStateRef}
      />
    </StyledPaper>
  );
};

export const MemoizedCardMediaImages = memo(CardMediaImages);
export { CardMediaImages };
